import Axios from "axios";
import { useState } from "react";
import { useParams } from 'react-router-dom';
import * as React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import {
    CloudArrowUpIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    DocumentIcon,
    EyeIcon,
    PaperClipIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline';

// ── Status badge config ────────────────────────────────────────────────────────
const getStatusCfg = (status, isFrench) => ({
    uploaded:       { label: isFrench ? 'Reçu'         : 'Received',     icon: CheckCircleIcon,        cls: 'text-emerald-600 bg-emerald-50' },
    approved:       { label: isFrench ? 'Approuvé'     : 'Approved',     icon: CheckCircleIcon,        cls: 'text-emerald-600 bg-emerald-50' },
    review:         { label: isFrench ? 'En révision'  : 'Under Review', icon: ClockIcon,              cls: 'text-amber-600 bg-amber-50' },
    rejected:       { label: isFrench ? 'Refusé'       : 'Rejected',     icon: ExclamationCircleIcon,  cls: 'text-red-500 bg-red-50' },
    'not uploaded': { label: isFrench ? 'Requis'       : 'Required',     icon: ExclamationCircleIcon,  cls: 'text-red-500 bg-red-50' },
}[status] || { label: status, icon: DocumentIcon, cls: 'text-slate-500 bg-slate-100' });

// ── Component ──────────────────────────────────────────────────────────────────
const DocReview = (props) => {
    const backEndUrl = process.env.REACT_APP_SERVER_ADDRESS;
    const { id } = useParams();

    const [openBackDrop, setOpenBackDrop] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [openDemoModal, setOpenDemoModal] = React.useState(false);
    const [fileName, setFileName] = useState("");

    const isFrench = props.language === 'french';
    const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.doc', '.docx', '.pages'];

    const isPending  = props.status === 'not uploaded';
    const isUploaded = props.status === 'uploaded' || props.status === 'approved';
    const isDone     = isUploaded || props.status === 'review';

    const statusCfg = getStatusCfg(props.status, isFrench);
    const StatusIcon = statusCfg.icon;

    // ── Handlers (unchanged logic) ─────────────────────────────────────────────
    const onChangeFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            alert(isFrench
                ? 'Seuls les fichiers PDF, JPG, Word (.doc/.docx) et Pages sont acceptés.'
                : 'Only PDF, JPG, Word (.doc/.docx), and Pages files are accepted.');
            e.target.value = '';
            return;
        }
        setFileName(file);
    };

    const handleDownloader = (key) => {
        window.location.href = `${backEndUrl}/updateClient/download/${key}`;
    };

    const changeonclick = (e) => {
        if (props.storageUsed > 10737418240) {
            alert("Storage limit reached. Please contact your consultant for more information.");
            return;
        }
        if (fileName.size > 8388608) {
            alert("La taille du fichier ne doit pas dépasser 8 MB.");
            return;
        }
        e.preventDefault();
        setOpenBackDrop(true);
        const formData = new FormData();
        formData.append("doc_upload", fileName);
        formData.append("doc_id", props.rid);
        formData.append("client_id", id);
        formData.append("clientName", props.clientName);
        formData.append("storageUsed", props.storageUsed);
        Axios.put(`${backEndUrl}/updateClient/uploadAS3`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
        }).then(() => {
            setOpenBackDrop(false);
            window.location.reload();
        });
    };

    const handleViewDoc = async () => {
        if (props.isDemo || !props.path) {
            setOpenDemoModal(true);
            return;
        }
        try {
            const response = await fetch(`${backEndUrl}/updateClient/viewDoc`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ key: props.path }),
            });
            const data = await response.json();
            const newWindow = window.open();
            newWindow.location.href = data;
        } catch (error) {
            console.error('Error fetching pre-signed URL:', error);
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: 1301, position: 'fixed' }} open={openBackDrop}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <div className={`group bg-white rounded-[2rem] transition-all duration-300 ${
                isDone ? 'opacity-80' : 'shadow-xl shadow-slate-200/50'
            }`}>
                <div className="p-8 md:p-10">
                    <div className="flex flex-col md:flex-row gap-8">

                        {/* ── Info column ── */}
                        <div className="flex-1 space-y-3 min-w-0">

                            {/* Title + badge */}
                            <div className="flex items-center gap-3 flex-wrap">
                                <h3 className="text-xl font-bold text-slate-900">{props.name}</h3>
                                {(isPending || isUploaded) && (
                                    <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${statusCfg.cls}`}>
                                        <StatusIcon className="h-3 w-3" />
                                        {statusCfg.label}
                                    </span>
                                )}
                            </div>

                            {/* Description */}
                            {props.description && (
                                <p className="text-slate-500 text-sm leading-relaxed max-w-xl">
                                    {props.description}
                                </p>
                            )}


                            {/* Consultant attachment */}
                            {props.consultantAttachment !== "none" && (
                                <button
                                    onClick={() => handleDownloader(props.consultantAttachment)}
                                    className="flex items-center gap-2 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                                >
                                    <PaperClipIcon className="h-3.5 w-3.5" />
                                    {isFrench ? 'Voir document joint' : 'View attached document'}
                                </button>
                            )}

                            {/* Rejection reason */}
                            {props.reason && props.status === 'rejected' && (
                                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                    ⚠️ {props.reason}
                                </p>
                            )}

                            {/* Upload reminder */}
                            {isPending && fileName && (
                                <p className="text-sm text-blue-500 font-bold">
                                    {isFrench
                                        ? "N'oubliez pas de cliquer sur « Soumettre » !"
                                        : "Don't forget to click \"Submit Document\"!"}
                                </p>
                            )}
                        </div>

                        {/* ── Action column ── */}
                        <div className="w-full md:w-64 flex flex-col justify-center shrink-0">

                            {/* Upload zone */}
                            {isPending && (
                                <form onSubmit={changeonclick} encType="multipart/form-data">
                                    <div className="relative group/upload cursor-pointer">
                                        <input
                                            type="file"
                                            name={props.rid}
                                            accept=".pdf,.jpg,.jpeg,.doc,.docx,.pages"
                                            onChange={onChangeFile}
                                            className="absolute inset-0 opacity-0 cursor-pointer z-10 w-full h-full"
                                        />
                                        <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all ${
                                            fileName
                                                ? 'border-indigo-300 bg-indigo-50/40'
                                                : 'border-slate-200 group-hover/upload:border-indigo-300 group-hover/upload:bg-indigo-50/20'
                                        }`}>
                                            <CloudArrowUpIcon className={`h-8 w-8 mx-auto mb-2 transition-all ${
                                                fileName
                                                    ? 'text-indigo-500'
                                                    : 'text-slate-300 group-hover/upload:text-indigo-400 group-hover/upload:scale-110'
                                            }`} />
                                            <p className={`text-xs font-bold truncate px-2 ${
                                                fileName ? 'text-indigo-600' : 'text-slate-400 group-hover/upload:text-indigo-500'
                                            }`}>
                                                {fileName ? fileName.name : (isFrench ? 'Cliquer pour téléverser' : 'Click or drag to upload')}
                                            </p>
                                            <p className="text-[10px] text-slate-300 mt-1">PDF, JPG, Word, Pages — 8 MB max</p>
                                        </div>
                                    </div>

                                    {fileName && (
                                        <button
                                            type="button"
                                            onClick={() => setOpen(true)}
                                            className="mt-3 w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold py-3 px-4 rounded-2xl shadow-lg shadow-indigo-200/60 transition-all active:scale-[0.98]"
                                        >
                                            {isFrench ? 'Soumettre' : 'Submit Document'}
                                            <ChevronRightIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </form>
                            )}

                            {/* View uploaded doc */}
                            {isUploaded && (
                                <button
                                    onClick={handleViewDoc}
                                    className="flex items-center justify-center gap-2 py-4 px-6 rounded-2xl text-slate-400 hover:text-sky-600 hover:bg-sky-50 transition-all text-xs font-bold"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                    {isFrench ? 'Voir mon document' : 'View Document'}
                                    <ChevronRightIcon className="h-3 w-3" />
                                </button>
                            )}
                        </div>

                    </div>
                </div>
            </div>

            {/* Demo-mode modal */}
            <Dialog
                open={openDemoModal}
                onClose={() => setOpenDemoModal(false)}
                PaperProps={{ sx: { borderRadius: '20px', px: 1, py: 0.5, minWidth: 380, maxWidth: 440 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.3rem' }}>🔒</span>
                    {isFrench ? 'Mode démonstration' : 'Demo mode'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569', lineHeight: 1.8, fontSize: '0.95rem' }}>
                        {isFrench
                            ? 'Ceci est une simulation.\nLe document réel de votre client s\'affichera ici en haute résolution.'
                            : 'This is a simulation.\nYour client\'s actual document will be displayed here in full resolution.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        onClick={() => setOpenDemoModal(false)}
                        variant="contained"
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, background: '#0ea5e9', boxShadow: '0 4px 14px 0 rgba(14,165,233,0.35)', '&:hover': { background: '#0284c7' } }}
                    >
                        {isFrench ? 'Compris' : 'Got it'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm dialog */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{ sx: { borderRadius: '20px', px: 1, py: 0.5, minWidth: 420 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem', pb: 0.5 }}>
                    {isFrench ? 'Téléverser ce document ?' : 'Upload this document?'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#475569', lineHeight: 1.7 }}>
                        {isFrench
                            ? `Êtes-vous sûr de vouloir transmettre '${fileName.name}' ?`
                            : `Are you sure you want to submit '${fileName.name}'?`}
                        <br /><br />
                        {isFrench
                            ? "Assurez-vous qu'il s'agit du bon document, complet et conforme aux exigences."
                            : "Make sure this is the correct document, complete and meeting all requirements."}
                        <br /><br />
                        <strong style={{ color: '#dc2626' }}>
                            {isFrench
                                ? "AVERTISSEMENT : Une fois soumis, vous ne pourrez pas en téléverser un autre."
                                : "WARNING: Once submitted, you won't be able to upload another file."}
                        </strong>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button
                        onClick={() => setOpen(false)}
                        variant="outlined"
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', background: '#f8fafc' } }}
                    >
                        {isFrench ? 'Annuler' : 'Cancel'}
                    </Button>
                    <Button
                        onClick={changeonclick}
                        variant="contained"
                        autoFocus
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, background: '#0ea5e9', boxShadow: '0 4px 14px 0 rgba(14,165,233,0.35)', '&:hover': { background: '#0284c7', boxShadow: '0 4px 14px 0 rgba(14,165,233,0.45)' } }}
                    >
                        {isFrench ? 'Soumettre' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DocReview;
